import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { authApi, User, ApiError, LoginResult } from '../services/api';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  login: (email: string, password: string, clientId?: string) => Promise<LoginResult>;
  verifyLoginCode: (email: string, code: string) => Promise<LoginResult>;
  verifyLogin2FA: (email: string, code: string) => Promise<LoginResult>;
  resendLoginCode: (email: string) => Promise<void>;
  signup: (data: {
    email: string;
    password: string;
    firstName?: string;
    lastName?: string;
  }) => Promise<{ email: string; requiresActivation: boolean }>;
  activateAccount: (email: string, activationToken: string) => Promise<void>;
  resendActivation: (email: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    authApi
      .getMe()
      .then(({ user }) => setUser(user ?? null))
      .catch(() => setUser(null))
      .finally(() => setIsLoading(false));
  }, []);

  const login = useCallback(async (email: string, password: string, clientId?: string): Promise<LoginResult> => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authApi.login(email, password, clientId);
      if (response.requiresLoginCode) {
        return { requiresLoginCode: true, requires2FA: false, email: response.email || email };
      }
      if (response.requires2FA) {
        return { requiresLoginCode: false, requires2FA: true, email: response.email || email };
      }
      if (response.user) {
        setUser(response.user);
        return { requiresLoginCode: false, requires2FA: false, role: response.user.role };
      }
      return { requiresLoginCode: false, requires2FA: false };
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Login failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyLoginCode = useCallback(async (email: string, code: string): Promise<LoginResult> => {
    setError(null);
    setIsLoading(true);
    try {
      const response = await authApi.verifyLoginCode(email, code);
      if (response.requires2FA) {
        return { requiresLoginCode: false, requires2FA: true, email: response.email || email };
      }
      if (response.user) {
        setUser(response.user);
        return { requiresLoginCode: false, requires2FA: false, role: response.user.role };
      }
      return { requiresLoginCode: false, requires2FA: false };
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Verification failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const verifyLogin2FA = useCallback(async (email: string, code: string): Promise<LoginResult> => {
    setError(null);
    setIsLoading(true);
    try {
      const { user } = await authApi.verifyLogin2FA(email, code);
      setUser(user);
      return { requiresLoginCode: false, requires2FA: false, role: user.role };
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Verification failed';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  const resendLoginCode = useCallback(async (email: string) => {
    setError(null);
    try {
      await authApi.resendLoginCode(email);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Failed to resend code';
      setError(message);
      throw err;
    }
  }, []);

  const signup = useCallback(
    async (data: {
      email: string;
      password: string;
      firstName?: string;
      lastName?: string;
    }) => {
      setError(null);
      setIsLoading(true);
      try {
        const response = await authApi.signup(data);
        return { email: response.email, requiresActivation: Boolean(response.requiresActivation) };
      } catch (err) {
        const message = err instanceof ApiError ? err.message : 'Signup failed';
        setError(message);
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    []
  );

  const activateAccount = useCallback(async (email: string, activationToken: string) => {
    setError(null);
    try {
      await authApi.activateAccount(email, activationToken);
    } catch (err) {
      const message = err instanceof ApiError ? err.message : 'Activation failed';
      setError(message);
      throw err;
    }
  }, []);

  const resendActivation = useCallback(async (email: string) => {
    setError(null);
    try {
      await authApi.resendActivation(email);
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : 'Failed to resend activation email';
      setError(message);
      throw err;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors
    }
    setUser(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const { user } = await authApi.getMe();
      setUser(user);
    } catch {
      setUser(null);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        error,
        login,
        verifyLoginCode,
        verifyLogin2FA,
        resendLoginCode,
        signup,
        activateAccount,
        resendActivation,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
